import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthStore } from '../auth/auth.store';
import { isTokenExpired } from '../auth/jwt';
import { TokenRefreshService } from '../auth/token-refresh.service';

/**
 * Functional HTTP interceptor that injects JWT from localStorage
 * into the Authorization header and handles 401 with refresh-retry.
 *
 * Core layer — infrastructure concern.
 *
 * Behaviors:
 *   - Injects JWT from localStorage into Authorization header
 *   - Sets withCredentials: true for /auth/* URLs (HttpOnly cookie forwarding)
 *   - On 401: delegates to TokenRefreshService.handle401()
 *   - Retries original request with new token after successful refresh
 *   - On refresh failure: navigates to /login?session_expired=1
 *   - Skip refresh for /auth/refresh endpoint (avoid infinite loop)
 *   - 403/404 branch-scope (BRANCH_ASSIGNMENT_REQUIRED, BRANCH_NOT_ASSIGNED, NOT_FOUND)
 *     are NOT handled here — they are mapped to ForbiddenStore same-URL steel panel
 *     by domain services via extractForbiddenCode. No redirect to /login nor /payments,
 *     no navigation, no GET extra, byte-identical 404 anti-enumeration.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenRefreshService = inject(TokenRefreshService);
  const authStore = inject(AuthStore);
  const router = inject(Router);

  const token = authStore.accessToken();

  // Build clone options: Bearer header (skip for /auth/refresh) + withCredentials for /auth/*
  const needsToken = !req.url.includes('/auth/refresh') && !!token;
  const needsCredentials = req.url.includes('/auth/');

  const cloned = req.clone({
    ...(needsToken ? { setHeaders: { Authorization: `Bearer ${token}` } } : {}),
    ...(needsCredentials ? { withCredentials: true } : {}),
  });

  return next(cloned).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401) {
        return throwError(() => error);
      }

      // Skip refresh-retry for /auth/login — 401 there means invalid credentials,
      // not an expired session. The login component handles its own error display.
      if (req.url.includes('/auth/login')) {
        return throwError(() => error);
      }

      // Phase 53 — onboarding incomplete: avoid refresh loop DoS (D-06)
      // If token expired, allow refresh attempt — expired onboarding should go to login via refresh failure
      if (authStore.needsOnboarding() && !isTokenExpired(authStore.accessToken())) {
        return throwError(() => error);
      }

      // Delegate to TokenRefreshService — handles concurrent queuing
      return tokenRefreshService.handle401().pipe(
        switchMap((newToken) => {
          // Retry original request with new token
          const retry = cloned.clone({
            setHeaders: { Authorization: `Bearer ${newToken}` },
          });
          return next(retry);
        }),
        catchError((refreshError) => {
          // Refresh failed — navigate to login with session_expired param
          router.navigate(['/login'], {
            queryParams: { session_expired: '1' },
          });
          return throwError(() => refreshError);
        })
      );
    })
  );
};
